// Initial shared state
const indexBannerState = {
  currentScrollTop: 0,
  headerBounds: null,
  firstScrollTop: 0,
  isInitialized: false,
  lastScrollDirection: null,
  suppressStateCheckUntil: 0,
  headerSection: null,
  indexBannerSection: null,
  productTabsHeader: null,
  enhancedScrollHandler: null,
  headerStateObserver: null,
};

// Used in the liquid file directly
function scrollToTop() {
  window.scrollTo({
    top: 0,
    behavior: "smooth",
  });
}

const initIndexBanner = () => {
  const indexBannerSection = document
    .querySelector(".pdp-index-banner")
    ?.closest(".shopify-section");

  const header = document.querySelector('header[is="sticky-header"]');
  const headerSection = document.querySelector(".header-section");

  const productTabsHeader = document.querySelector(
    ".product-details-tabs__header"
  );

  // Store references in shared state
  indexBannerState.headerSection = headerSection;
  indexBannerState.indexBannerSection = indexBannerSection;
  indexBannerState.productTabsHeader = productTabsHeader;

  if (indexBannerSection && header && headerSection) {
    const isDesktop = () => window.innerWidth >= 768;

    // This should only run on desktop
    if (!isDesktop()) return;

    const indexBannerElement =
      indexBannerSection.querySelector(".pdp-index-banner");
    const enableHeaderHiding =
      indexBannerElement?.getAttribute("data-enable-header-hiding") === "true";

    // Mark header section to indicate index banner is present
    headerSection.setAttribute("data-has-index-banner", "true");

    // If header hiding is disabled, disable the original sticky header behavior and exit
    if (!enableHeaderHiding) {
      requestAnimationFrame(() => {
        indexBannerSection.classList.add("index-banner-ready");
      });

      // Completely disable the original sticky header behavior
      if (header) {
        // Override the scroll handler to prevent header hiding
        if (header.onScrollHandler) {
          header._originalScrollHandler = header.onScrollHandler;
          header.onScrollHandler = () => {
            const scrollTop = window.scrollY;

            if (!header.headerBounds) {
              header.headerBounds = headerSection.getBoundingClientRect();
              header.firstScrollTop = scrollTop;
            }

            if (
              header.headerBounds &&
              scrollTop >
                header.headerBounds.top +
                  header.firstScrollTop +
                  header.headerBounds.height
            ) {
              headerSection.classList.add("header-scrolled");
              document.documentElement.style.setProperty(
                "--sticky-header-height",
                Math.round(header.clientHeight) + "px"
              );
              document.dispatchEvent(
                new CustomEvent("header:scrolled", {
                  bubbles: true,
                  detail: { scrolled: true },
                })
              );

              if (
                scrollTop >
                header.headerBounds.top +
                  header.firstScrollTop +
                  header.headerBounds.height * 2
              ) {
                headerSection.classList.add("header-nav-scrolled");
              }
            } else {
              headerSection.classList.remove("header-scrolled");
              headerSection.classList.remove("header-nav-scrolled");
              document.dispatchEvent(
                new CustomEvent("header:scrolled", {
                  bubbles: true,
                  detail: { scrolled: false },
                })
              );
            }

            headerSection.classList.remove("header-hidden");
            header.currentScrollTop = scrollTop;
          };
        }

        headerSection.classList.remove("header-hidden");

        // Set up a watcher to prevent header-hidden from being added
        const preventHeaderHiding = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            if (
              mutation.type === "attributes" &&
              mutation.attributeName === "class"
            ) {
              if (
                mutation.target === headerSection &&
                headerSection.classList.contains("header-hidden")
              ) {
                headerSection.classList.remove("header-hidden");
              }
            }
          });
        });

        preventHeaderHiding.observe(headerSection, {
          attributes: true,
          attributeFilter: ["class"],
        });
      }

      return;
    }

    // Scroll behavior for Index Banner - replaces the default sticky header behavior
    indexBannerState.currentScrollTop = window.scrollY;
    indexBannerState.headerBounds = null;
    indexBannerState.firstScrollTop = 0;
    indexBannerState.isInitialized = false;
    indexBannerState.lastScrollDirection = null;
    indexBannerState.suppressStateCheckUntil = 0;

    // Sync function to keep index banner in sync with header state
    const syncIndexBannerState = (shouldBeHidden) => {
      if (shouldBeHidden) {
        indexBannerSection.classList.add("header-hidden");
        if (productTabsHeader) {
          productTabsHeader.classList.add("header-is-hidden");
        }
      } else {
        indexBannerSection.classList.remove("header-hidden");
        if (productTabsHeader) {
          productTabsHeader.classList.remove("header-is-hidden");
        }
      }
    };

    // Watch header-section for class changes and sync index banner
    indexBannerState.headerStateObserver = new MutationObserver((mutations) => {
      // Use requestAnimationFrame to batch rapid changes and avoid excessive DOM updates
      requestAnimationFrame(() => {
        mutations.forEach((mutation) => {
          if (
            mutation.type === "attributes" &&
            mutation.attributeName === "class" &&
            mutation.target === headerSection
          ) {
            const isHeaderHidden =
              headerSection.classList.contains("header-hidden");
            const indexBannerIsHidden =
              indexBannerSection.classList.contains("header-hidden");

            // Only sync if states don't match (prevents infinite loops)
            if (isHeaderHidden !== indexBannerIsHidden) {
              syncIndexBannerState(isHeaderHidden);
            }

            // Also sync product tabs header if it exists
            if (productTabsHeader) {
              const tabsIsHidden =
                productTabsHeader.classList.contains("header-is-hidden");
              if (isHeaderHidden !== tabsIsHidden) {
                if (isHeaderHidden) {
                  productTabsHeader.classList.add("header-is-hidden");
                } else {
                  productTabsHeader.classList.remove("header-is-hidden");
                }
              }
            }
          }
        });
      });
    });

    // Start observing header-section immediately
    indexBannerState.headerStateObserver.observe(headerSection, {
      attributes: true,
      attributeFilter: ["class"],
      attributeOldValue: false,
    });

    // Function to mark banner as ready and show it
    const markBannerReady = () => {
      if (!indexBannerSection.classList.contains("index-banner-ready")) {
        requestAnimationFrame(() => {
          indexBannerSection.classList.add("index-banner-ready");
        });
      }
    };

    // Define enhanced scroll handler first
    indexBannerState.enhancedScrollHandler = () => {
      // Don't process until initialization is complete (prevents visual jumps during init)
      if (!indexBannerSection.classList.contains("index-banner-ready")) return;

      if (!indexBannerState.headerBounds || !indexBannerState.isInitialized)
        return;

      // Don't process if we're suppressing state checks (e.g., after nav click)
      if (performance.now() < indexBannerState.suppressStateCheckUntil) return;

      const scrollTop = window.scrollY;
      const hideThreshold = indexBannerState.headerBounds.height + 50;
      const scrollDelta = scrollTop - indexBannerState.currentScrollTop;

      // Track scroll direction
      // If scrollDelta is 0, keep last known direction
      if (scrollDelta > 0) {
        indexBannerState.lastScrollDirection = "down";
      } else if (scrollDelta < 0) {
        indexBannerState.lastScrollDirection = "up";
      }

      let shouldHideHeader = false;

      if (scrollTop > hideThreshold) {
        // For fast scrolling, use a more sensitive threshold
        const scrollSpeed = Math.abs(scrollDelta);
        const isFastScroll = scrollSpeed > 50;

        if (scrollDelta > 0) {
          // Scrolling down
          shouldHideHeader = true;
        } else if (scrollDelta < 0) {
          // Scrolling up - always show header
          shouldHideHeader = false;
        } else {
          // No scroll change - maintain current state based on last direction
          if (indexBannerState.lastScrollDirection === "up") {
            shouldHideHeader = false;
          } else {
            shouldHideHeader = scrollTop > hideThreshold;
          }
        }

        // For very fast scrolling, ensure immediate response
        if (isFastScroll) {
          if (scrollDelta > 0) {
            shouldHideHeader = true;
          } else if (scrollDelta < 0) {
            shouldHideHeader = false;
          }
        }
      } else {
        // Above threshold, always show header
        shouldHideHeader = false;
      }

      // Apply changes only if state actually changed to avoid unnecessary DOM updates
      const currentHeaderHidden =
        headerSection.classList.contains("header-hidden");
      if (shouldHideHeader !== currentHeaderHidden) {
        if (shouldHideHeader) {
          headerSection.classList.add("header-hidden");
        } else {
          headerSection.classList.remove("header-hidden");
        }
      }

      indexBannerState.currentScrollTop = scrollTop;
    };

    // Get header bounds after initialization with better timing
    const initializeBounds = () => {
      const bounds = headerSection.getBoundingClientRect();
      const headerHeight = bounds.height;
      indexBannerState.headerBounds = {
        ...bounds,
        height: headerHeight,
        absoluteBottom: headerHeight,
      };
      indexBannerState.isInitialized = true;
    };

    const finalizeInitialization = () => {
      if (!indexBannerState.headerBounds || !indexBannerState.isInitialized) {
        return false;
      }

      const scrollTop = window.scrollY;
      const hideThreshold = indexBannerState.headerBounds.height + 50;

      const shouldHideHeader = scrollTop > hideThreshold;

      if (
        shouldHideHeader !== headerSection.classList.contains("header-hidden")
      ) {
        if (shouldHideHeader) {
          headerSection.classList.add("header-hidden");
        } else {
          headerSection.classList.remove("header-hidden");
        }
      }

      syncIndexBannerState(shouldHideHeader);

      indexBannerState.currentScrollTop = scrollTop;
      indexBannerState.lastScrollDirection = scrollTop > 0 ? "down" : null;

      // Mark banner as ready - this enables transitions and scroll handler
      markBannerReady();
      return true;
    };

    // More aggressive approach to disable original sticky header behavior
    if (header) {
      // Store original methods
      if (header.onScrollHandler) {
        header._originalScrollHandler = header.onScrollHandler;
        header.onScrollHandler = () => {};
      }

      // Override the entire sticky header scroll behavior
      if (header.beforeInit) {
        header._originalBeforeInit = header.beforeInit;
        header.beforeInit = () => {};
      }

      // Remove any existing scroll listeners that might interfere
      const allScrollListeners = window._scrollListeners || [];
      allScrollListeners.forEach((listener) => {
        if (
          listener.toString().includes("header-hidden") ||
          listener.toString().includes("onScrollHandler")
        ) {
          window.removeEventListener("scroll", listener);
        }
      });
    }

    // Use throttled scroll handler with same timing as original
    let ticking = false;
    const throttledScrollHandler = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          if (indexBannerState.enhancedScrollHandler) {
            indexBannerState.enhancedScrollHandler();
          }
          ticking = false;
        });
        ticking = true;
      }
    };

    // Add our scroll handler
    window.addEventListener("scroll", throttledScrollHandler, {
      passive: true,
    });

    // Handle page refresh/reload scenarios - sync initial state
    window.addEventListener("load", () => {
      setTimeout(() => {
        initializeBounds();
        finalizeInitialization();
      }, 100);
    });

    // Also check initial state immediately if DOM is already ready
    if (
      document.readyState === "complete" ||
      document.readyState === "interactive"
    ) {
      setTimeout(() => {
        initializeBounds();
        finalizeInitialization();
      }, 100);
    }

    window.addEventListener("resize", () => {
      setTimeout(() => {
        if (isDesktop()) {
          initializeBounds();
          if (indexBannerState.isInitialized) {
            if (indexBannerState.enhancedScrollHandler) {
              indexBannerState.enhancedScrollHandler();
            }
          }
        }
      }, 100);

      // If we switch to mobile, clean up and exit
      if (!isDesktop()) {
        if (headerSection) {
          headerSection.classList.remove("header-hidden");
        }
        if (indexBannerSection) {
          indexBannerSection.classList.remove("header-hidden");
        }
        if (productTabsHeader) {
          productTabsHeader.classList.remove("header-is-hidden");
        }
        if (indexBannerState.headerStateObserver) {
          indexBannerState.headerStateObserver.disconnect();
        }
        return;
      }
    });
  }
};

// Navigation/Scroll-spy functionality
function setupNavigation() {
  const linkClass = ".pdp-index-banner__nav-link";

  const navLinks = Array.from(
    document.querySelectorAll(`${linkClass}[data-scroll-target]`)
  );
  const targets = navLinks
    .map((link) => {
      const id = link.getAttribute("data-scroll-target");
      const el =
        id &&
        (document.getElementById(id) ||
          document.querySelector(`[data-section-id="${id}"]`));
      return el ? { id, el } : null;
    })
    .filter(Boolean);

  // Suppress scroll-based updates briefly after a manual click to avoid flicker
  let suppressUpdatesUntil = 0;
  const SUPPRESS_MS = 1000;

  function updateActive() {
    if (performance.now() < suppressUpdatesUntil) return;

    const docStyles = getComputedStyle(document.documentElement);
    const stickyHeaderHeight =
      parseInt(docStyles.getPropertyValue("--sticky-header-height")) || 0;
    const indexBannerHeight =
      parseInt(docStyles.getPropertyValue("--pdp-index-banner-height")) || 0;

    const currentOffset = stickyHeaderHeight + indexBannerHeight;
    const viewportHeight =
      window.innerHeight || document.documentElement.clientHeight;

    navLinks.forEach((link) => link.classList.remove("active"));

    // Collect visible sections within the effective viewport (below fixed headers)
    const visible = [];
    targets.forEach((t) => {
      const rect = t.el.getBoundingClientRect();
      const effTop = rect.top - currentOffset;
      const effBottom = rect.bottom - currentOffset;
      const isVisible = effTop < viewportHeight && effBottom > 0;

      if (isVisible) {
        const sectionHeight = rect.height || Math.max(1, rect.bottom - rect.top);
        const intersectionTop = Math.max(effTop, 0);
        const intersectionBottom = Math.min(effBottom, viewportHeight);
        const visibleHeight = Math.max(0, intersectionBottom - intersectionTop);
        const ratio = visibleHeight / sectionHeight;
        visible.push({ id: t.id, effTop, visibleHeight, ratio });
      }
    });

    let chosenId = null;
    if (visible.length > 0) {
      const MIN_VISIBLE_RATIO = 0.35;

      // First, consider only sections meeting minimum visibility ratio
      let candidates = visible.filter((v) => v.ratio >= MIN_VISIBLE_RATIO);

      let candidate;
      if (candidates.length > 0) {
        // Choose the one with highest visible ratio; tie-breaker: closest to top
        candidate = candidates.sort((a, b) => {
          if (b.ratio !== a.ratio) return b.ratio - a.ratio;
          return Math.abs(a.effTop) - Math.abs(b.effTop);
        })[0];
      } else {
        // Fallback: choose the one with greatest absolute visible height
        candidate = visible.sort((a, b) => {
          if (b.visibleHeight !== a.visibleHeight)
            return b.visibleHeight - a.visibleHeight;
          return Math.abs(a.effTop) - Math.abs(b.effTop);
        })[0];
      }

      chosenId = candidate?.id || null;
    }

    if (chosenId) {
      const activeLink = document.querySelector(
        `${linkClass}[data-scroll-target="${chosenId}"]`
      );
      activeLink?.classList.add("active");
    }
  }

  // Throttle with rAF
  let ticking = false;
  function onScrollOrResize() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      updateActive();
      ticking = false;
    });
  }

  window.addEventListener("scroll", onScrollOrResize, { passive: true });
  window.addEventListener("resize", onScrollOrResize);
  updateActive();

  // Click-to-scroll with immediate active highlight
  navLinks.forEach((link) => {
    link.addEventListener("click", (e) => {
      const id = link.getAttribute("data-scroll-target");
      if (!id) return;

      const targetEl =
        document.getElementById(id) ||
        document.querySelector(`[data-section-id="${id}"]`);
      if (!targetEl) return;

      // Only intercept internal scroll targets (anchors)
      const href = link.getAttribute("href") || "";
      if (!href.startsWith("#")) return;

      // Prevent default jump
      e.preventDefault();

      // Immediate active highlight
      navLinks.forEach((l) => l.classList.remove("active"));
      link.classList.add("active");

      // Compute offset and scroll smoothly
      const docStyles = getComputedStyle(document.documentElement);
      const stickyHeaderHeight =
        parseInt(docStyles.getPropertyValue("--sticky-header-height")) || 0;
      const indexBannerHeight =
        parseInt(docStyles.getPropertyValue("--pdp-index-banner-height")) || 0;

      const offset = stickyHeaderHeight + indexBannerHeight;
      const targetTop =
        targetEl.getBoundingClientRect().top + window.pageYOffset - offset;

      suppressUpdatesUntil = performance.now() + SUPPRESS_MS;

      // Determine scroll direction
      const currentScroll = window.scrollY;
      const willScrollUp = targetTop < currentScroll;

      // Suppress automatic state checks for a period after nav click to prevent jumping
      if (indexBannerState) {
        indexBannerState.suppressStateCheckUntil = performance.now() + 1200;

        if (willScrollUp) {
          indexBannerState.lastScrollDirection = "up";
        } else {
          indexBannerState.lastScrollDirection = "down";
        }
      }

      window.scrollTo({ top: targetTop, behavior: "smooth" });

      // After scroll completes, ensure header state is correct
      setTimeout(() => {
        if (indexBannerState) {
          indexBannerState.currentScrollTop = window.scrollY;

          if (willScrollUp) {
            // If we scrolled up, ensure header is visible
            if (indexBannerState.headerSection) {
              indexBannerState.headerSection.classList.remove("header-hidden");
            }
          } else {
            // If we scrolled down, check state normally via scroll handler
            if (indexBannerState.enhancedScrollHandler) {
              indexBannerState.enhancedScrollHandler();
            }
          }
          // Clear suppression after handling
          indexBannerState.suppressStateCheckUntil = 0;
        }
      }, 800);
    });
  });
}

/**
 * Klaviyo CTA trigger (opens form on click)
 * - Binds directly to `.klaviyo_form_trigger`
 * - Capture phase to beat other handlers
 * - Prevents navigation + stops propagation
 * - Opens on next frame to avoid immediate close/hijack
 */
function initKlaviyoCTATrigger() {
  const triggers = document.querySelectorAll(".klaviyo_form_trigger");
  if (!triggers.length) return;

  triggers.forEach((el) => {
    if (el.dataset.nwKlaviyoBound === "1") return;
    el.dataset.nwKlaviyoBound = "1";

    el.addEventListener(
      "click",
      function (e) {
        e.preventDefault();
        e.stopPropagation();
        if (typeof e.stopImmediatePropagation === "function") {
          e.stopImmediatePropagation();
        }

        console.log("Klaviyo CTA clicked (bound direct)");

        try {
          // Run after click stack clears (prevents open->close from other click listeners)
          requestAnimationFrame(() => {
            if (window._klOnsite && typeof window._klOnsite.openForm === "function") {
              window._klOnsite.openForm("SEtrUV");
              return;
            }

            window._klOnsite = window._klOnsite || [];
            window._klOnsite.push(["openForm", "SEtrUV"]);
          });
        } catch (err) {
          console.error("Klaviyo openForm failed:", err);
        }
      },
      true
    );
  });
}

function initFullIndexBanner() {
  initIndexBanner();
  setupNavigation();
  initKlaviyoCTATrigger();
}

// Run once on initial page load
theme?.DOMready(initFullIndexBanner);

// Shopify Theme Editor support
if (Shopify.designMode) {
  document.addEventListener("shopify:section:load", initFullIndexBanner);
  document.addEventListener("shopify:section:load", initKlaviyoCTATrigger);
}
